import { useMsal } from '@azure/msal-react';
import { useEffect, useState } from 'react';
import { MAIN } from '@/configs/main.config';
import {
  chargeMSALTokenProcess,
  setupRefreshTokenTimeOut,
} from '@/services/msal/getMSALToken';
import { SipJs } from '@/services/sip-js.service';
import { SolgariApiService } from '@/services/solgari-api/solgari-api.service';
import { WebApi } from '@/services/web-api/web-api.service';
import { getExtensionFromUri } from '@/shared/helpers/get-from-uri.helper';
import { IAuthData } from '@/shared/interfaces/auth-data.interface';
import { useStore } from '@/stores';
import { getStoredDynToken } from '@/utils/getMSALToken';
import { TenantInfo } from '@/services/solgari-api/interfaces/tenant-info';
import { FeatureFlags } from '@/services/solgari-api/interfaces/feature-flags.interface';
import { logger } from '@/services/logger.service';
import { BusEvent, EBusEvent } from '@/services/bus-event/bus-event.service';
import { loginRequest } from '@/services/msal/authConfig';
import { Auth } from '@/services/auth.service';

export const useLogin = () => {
  // INFO: is sip logged (final logged status)
  const [isLogged, setIsLogged] = useState(false);
  const [presenceStatus, setPresenceStatus] = useState(null);
  const { instance, accounts } = useMsal();
  const {
    settingsStore: {
      setInitializationState,
      initializationState,
      setTenantInfo,
      setFeatureFlags,
    },
  } = useStore();

  // In order to tun the application we need third party cookies to be allowed
  // So we try to setup them manually with 'SameSite=None' and check if we can access them.
  // Afterwards we clean them up
  const checkThirdPartyCookies = () => {
    return new Promise((resolve) => {
      document.cookie = 'thirdPartyCookieTest=1; SameSite=None; Secure';

      const cookiesEnabled = document.cookie.includes('thirdPartyCookieTest=1');

      document.cookie =
        'thirdPartyCookieTest=1; SameSite=None; Secure; expires=Thu, 01 Jan 1970 00:00:00 UTC';

      resolve(cookiesEnabled);
    });
  };

  // We can't start the application without browser microphone permitted
  const checkMicrophonePermissions = async () => {
    try {
      return await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setInitializationState(() => {
        return {
          percent: initializationState.percent,
          prompt: {
            ...initializationState.prompt,
            sipLogin: { text: 'Stop initialization', color: 'danger' },
            dynLogin: {
              text: 'Browser microphone access denied',
              color: 'danger',
            },
          },
        };
      });
    }
  };

  const dynVerifiedView = () => {
    setInitializationState(() => {
      return {
        percent: initializationState.percent + 30,
        prompt: {
          ...initializationState.prompt,
          dynLogin: {
            text: 'Dynamics 365 verification succeeded',
            color: 'success',
          },
        },
      };
    });
  };

  const getInitialWrapUp = async (
    authData: IAuthData,
    tenantInfo: TenantInfo,
  ) => {
    localStorage.setItem(MAIN.lsTenantIdKey, authData?.tenantId?.toString());
    localStorage.setItem(
      MAIN.lsAutoAnswerToggleKey,
      tenantInfo?.autoAnswer?.toString() ?? 'false',
    );
    localStorage.setItem(
      MAIN.lsAutoAnswerTimerKey,
      tenantInfo?.autoAnswerDelayInSeconds.toString(),
    );
    localStorage.setItem(
      MAIN.lsWrapUpToggleAllCallsKey,
      tenantInfo?.wrapUpTimeForDirectCallsInSeconds.toString(),
    );
  };

  const runLogin = async (dynToken?: string) => {
    if (!dynToken) {
      const response = await instance.acquireTokenPopup({
        ...loginRequest(),
      });
      Auth.saveDynToken(response.accessToken);
      Auth.saveMSALToken(response.accessToken);
    }

    const thirdPartyCookiesEnabled = await checkThirdPartyCookies();

    if (!thirdPartyCookiesEnabled) {
      setInitializationState(() => {
        return {
          percent: initializationState.percent,
          prompt: {
            ...initializationState.prompt,
            sipLogin: { text: 'Stop initialization', color: 'danger' },
            dynLogin: {
              text: 'Browser third party cookies are disabled',
              color: 'danger',
            },
          },
        };
      });
      return;
    }

    const microphonePermission = await checkMicrophonePermissions();

    if (!microphonePermission) {
      return;
    }

    setupRefreshTokenTimeOut(refreshLoginData);

    WebApi.setMSALInstance(instance);
    await WebApi.init();
    dynVerifiedView();

    SolgariApiService.connectToSignalR();

    const token = await getStoredDynToken();

    if (!token) {
      return;
    }

    const authData = await SolgariApiService.getAuthData();

    if (!authData) {
      return;
    }

    setInitializationState(() => {
      return {
        percent: 80,
        prompt: {
          ...initializationState.prompt,
          sipLogin: { text: 'Registering SIP user...', color: 'secondary' },
        },
      };
    });

    SipJs.init(authData);
    const tenantInfo: TenantInfo = await SolgariApiService.getTenantInfo();
    setTenantInfo(tenantInfo);
    const featureFlags: FeatureFlags[] =
      await SolgariApiService.getFeatureFlags(tenantInfo.tenantId);
    setFeatureFlags(featureFlags);
    getInitialWrapUp(authData, tenantInfo);
  };

  const refreshLoginData = async () => {
    const newToken = await chargeMSALTokenProcess({
      instance,
      account: accounts[0],
    });

    if (!newToken) {
      logger.error('[AUTH] Failed to refresh token');
      return;
    }

    setupRefreshTokenTimeOut(refreshLoginData);
  };

  const sipDevLogin = async (creds?: IAuthData) => {
    SipJs.init(creds);
  };

  useEffect(() => {
    const busSubscription = BusEvent.getBus$().subscribe((event) => {
      if (event?.event === EBusEvent.SILENT_REFRESH) {
        refreshLoginData();
      }
    });

    return () => {
      busSubscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!isLogged) {
      return;
    }

    const unSubPresence = SipJs.presences$.subscribe((presences) => {
      const mySelfPresence = presences.find(
        (p) => p.extension === getExtensionFromUri(SipJs.authData.uri),
      );
      if (!mySelfPresence) {
        return;
      }

      setPresenceStatus(mySelfPresence.status);
    });

    return () => {
      if (unSubPresence) {
        unSubPresence.unsubscribe();
        SipJs.unSubscribeAll();
      }
    };
  }, [isLogged]);

  return {
    runLogin,
    refreshLoginData,
    sipDevLogin,
    isLogged,
    setIsLogged,
    presenceStatus,
  };
};
